// TradeOrderChip — where the job's JAC sales order stands in Books (Oct 29, 2026): "Order in Books", "JAC invoiced ·
// matches" / "· differs", "Adjusted by Shannon: <reason>", "Paid Nov 6". On the trade card after Chad's margin chip,
// inside the price block (the visit's own price or its partner's), or on its own on an open visit of a JAC pair whose
// price isn't on screen (orderChipPlace); only the price viewers ever see it (trade_books_orders answers no rows to
// anyone else).
// Greg: "no need to show the amount bardin pays jac": no sub cost here, ever (the words are in lib/tradeOrderText).
//
// Every chip on a screen shares one store: the orders are read in one batch (fetchTradeOrders), and one realtime
// channel on trade_books_orders (migration 20261029h puts it in the publication) refreshes them while any chip is on
// screen, so what Books says shows on the Builds board's Trades view and the deal page without a reload. The trade
// screens themselves stay as they are.

import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "../../lib/supabase";
import { fetchTradeOrders, tradeJobKey, tradeOrderChip, TRADE_ORDER_TONE_CLASSES, type Trade, type TradeOrder } from "../../lib/trades";

/** null: read, and the job has no order. */
type Entry = TradeOrder | null;

const orders = new Map<string, Entry>();
const listeners = new Set<() => void>();
let queued = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let channel: ReturnType<typeof supabase.channel> | null = null;
let generation = 0;
let channelSeq = 0;

function emit() {
  for (const f of listeners) f();
}

async function load(keys: string[]) {
  if (!keys.length) return;
  const gen = generation;
  try {
    const got = await fetchTradeOrders(keys);
    if (gen !== generation) return;   // every chip left meanwhile: the next screen reads afresh
    for (const k of keys) orders.set(k, got[k] ?? null);
    emit();
  } catch (e) {
    // The chip stays as it was; the next change on the table, or the next screen, reads again.
    console.warn("[trades] couldn't read the Books orders:", e instanceof Error ? e.message : e);
  }
}

/** Ask for a job's order: read in one batch with the others asked for in the same render. */
function request(key: string) {
  if (orders.has(key) || queued.has(key)) return;
  queued.add(key);
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const keys = [...queued];
    queued = new Set();
    void load(keys);
  }, 0);
}

/** A change on trade_books_orders: read every order on screen again (a burst reads once). */
function refreshAll() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void load([...orders.keys()]);
  }, 500);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (!channel) {
    const ch = supabase.channel(`realtime-trade_books_orders-${Date.now()}-${++channelSeq}`);
    ch.on("postgres_changes" as never, { event: "*", schema: "public", table: "trade_books_orders" }, () => { refreshAll(); });
    ch.subscribe();
    channel = ch;
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size) return;
    if (channel) void supabase.removeChannel(channel);
    channel = null;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    orders.clear();
    generation += 1;
  };
}

function useTradeOrder(key: string): Entry | undefined {
  const order = useSyncExternalStore(subscribe, () => orders.get(key));
  useEffect(() => { request(key); }, [key]);
  return order;
}

export default function TradeOrderChip({ t }: { t: Pick<Trade, "id" | "pair_id" | "vendor"> }) {
  const chip = tradeOrderChip(useTradeOrder(tradeJobKey(t)), t.vendor);
  if (!chip) return null;
  return (
    <div className={`mt-1 ml-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${TRADE_ORDER_TONE_CLASSES[chip.tone]}`} title={chip.title}>
      {chip.label}
    </div>
  );
}
